// Settings — T060
import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings, sendTestEmail, type Settings as GlobalSettings } from '../services/api';
import { useAppSettings } from '../hooks/useAppSettings';

export default function Settings() {
  const { setAppTitle } = useAppSettings();
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [retention, setRetention] = useState('');
  const [title, setTitle] = useState('');
  const [confirmUnlimited, setConfirmUnlimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    getSettings()
      .then((s) => {
        setSettings(s);
        setRetention(s.retentionDays === 0 ? '0' : String(s.retentionDays));
        setTitle(s.appTitle || 'CM-Monitor');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const days = Number(retention);
    if (isNaN(days) || (days !== 0 && days < 30)) {
      setError('Retention must be 0 (unlimited) or ≥ 30 days');
      return;
    }
    if (days === 0 && !confirmUnlimited) {
      setError('Please confirm unlimited retention');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateSettings({ retentionDays: days, appTitle: title });
      setSettings(updated);
      setAppTitle(updated.appTitle || 'CM-Monitor');
      setSuccess('Settings saved');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse bg-surface rounded-lg h-32" />;

  return (
    <div className="max-w-lg mx-auto">
      <h1 className="text-white text-h1 mb-6">Settings</h1>

      <form onSubmit={handleSubmit} className="bg-surface rounded-lg p-6 border border-border space-y-4">
        {error && <div className="bg-status-down/20 text-status-down rounded px-3 py-2 text-sm">{error}</div>}
        {success && <div className="bg-status-up/20 text-status-up rounded px-3 py-2 text-sm">{success}</div>}

        <div>
          <label className="block text-text-secondary text-sm mb-1">Application Title</label>
          <input
            type="text"
            aria-label="Application Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={50}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <p className="text-text-muted text-xs mt-1">
            Displayed in the sidebar and header. Defaults to "CM-Monitor" if left empty.
          </p>
        </div>

        <div>
          <label className="block text-text-secondary text-sm mb-1">Metric Retention (days)</label>
          <input
            type="number"
            aria-label="Metric Retention"
            value={retention}
            onChange={(e) => { setRetention(e.target.value); setConfirmUnlimited(false); }}
            min={0}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <p className="text-text-muted text-xs mt-1">
            Set to 0 for unlimited, or ≥ 30 for automatic cleanup. Metrics older than this are purged via TTL.
          </p>
        </div>

        {retention === '0' && (
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={confirmUnlimited}
              onChange={(e) => setConfirmUnlimited(e.target.checked)}
              className="w-4 h-4 accent-brand"
            />
            I understand unlimited retention may increase storage costs
          </label>
        )}

        <button
          type="submit"
          disabled={saving}
          className="bg-brand text-white rounded px-4 py-2 text-sm min-h-[44px] disabled:opacity-50 hover:bg-brand-hover transition-colors"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>

        {settings && (
          <p className="text-text-muted text-xs">
            Last updated: {new Date(settings.updatedAt).toLocaleString()}
          </p>
        )}
      </form>

      {/* Test Email */}
      <div className="bg-surface rounded-lg p-6 border border-border space-y-4 mt-6">
        <h2 className="text-white text-lg font-semibold">Send Test Email</h2>
        <p className="text-text-muted text-sm">Send a test email through the same SendGrid pipeline used for monitor alerts.</p>

        {testResult && (
          <div className={`rounded px-3 py-2 text-sm ${
            testResult.type === 'success' ? 'bg-status-up/20 text-status-up' : 'bg-status-down/20 text-status-down'
          }`}>{testResult.message}</div>
        )}

        <div className="flex gap-2">
          <input
            type="email"
            aria-label="Test email address"
            placeholder="recipient@example.com"
            value={testEmail}
            onChange={(e) => { setTestEmail(e.target.value); setTestResult(null); }}
            className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            type="button"
            disabled={sendingTest || !testEmail.trim()}
            onClick={async () => {
              setSendingTest(true);
              setTestResult(null);
              try {
                await sendTestEmail(testEmail.trim());
                setTestResult({ type: 'success', message: `Test email sent to ${testEmail.trim()}` });
              } catch (err: any) {
                setTestResult({ type: 'error', message: err.message || 'Failed to send test email' });
              } finally {
                setSendingTest(false);
              }
            }}
            className="bg-brand text-white rounded px-4 py-2 text-sm min-h-[44px] disabled:opacity-50 hover:bg-brand-hover transition-colors"
          >
            {sendingTest ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
